import { meetTypeName } from 'utils/clubFeatures';
import React from 'react';
import {
  ageGroupOptions,
  fieldEventOptions,
  groupDistanceEventOptions,
  hurdleEventOptions,
  multiLaneSprintEventOptions,
  relayEventOptions,
  sprintAgilityTrainingOptions,
  statusOptions,
  walkEventOptions,
} from 'config/admin/meet';
import { getMeetAgeGroupLabels } from 'utils/meet';
import { GROUPING_BASIS, formatGroupHeading } from 'utils/groupLabel';
import { copyPointsRules, toPointsRulesRequest } from 'utils/pointsRules';

export const meetKeys = [
  'meetName',
  'date',
  'time',
  'description',
  'meetType',
  'location',
  'meetStatus',
  'timezone',
  'countsForSeasonPoints',
  'countsForSeasonBests',
  'useCustomPoints',
  // Meet signup config (§6.8)
  'selfServiceRegistrationEnabled',
  'publicSignupEnabled',
  'signupOpenDate',
  'signupOpenTime',
  'signupCloseDate',
  'signupCloseTime',
  'registrationCap',
];

/**
 * Transforms a result object into a detailed format for display or processing.
 *
 * This function takes a result object, converts its properties to more user-friendly formats, and adds derived fields.
 * Specifically, it maps `meetType` to a human-readable string, and formats `ageGroups` and `eventTypes` as comma-separated lists.
 *
 * @param {Meet} meet - The result object with the following properties:
 *   - {number} meetType - The type of the result (1 for single club competition, 2 for multi club competition, 3 for training). (typeof: number)
 *   - {Array<number>} ageGroups - An array of age group IDs. (typeof: Array<number>)
 *   - {Array<number>} eventTypes - An array of event type IDs. (typeof: Array<number>)
 *
 * @returns {MeetDetail} - A new result object containing the following properties:
 *   - All original result properties.
 *   - {string} ageGroups - A comma-separated string of age group labels.
 *   - {string} eventTypes - A comma-separated string of event type labels.
 */
// club (optional): the club the meet belongs to, which decides what the meet type is called. Without it the
// type is named as an athletics club reads it.
export const generateMeetDetail = (meet, isLabel2 = false, club = null) => {
  return {
    ...meet,
    meetTypeLabel: meetTypeName(club, meet.meetType, 'detail'),
    ageGroups: getMeetAgeGroupLabels(meet),
    eventTypes: getEventTypeLabel(meet.eventTypes, isLabel2).join(', '),
    seasonPointsLabel: meet.countsForSeasonPoints === false ? 'Excluded' : 'Included',
    seasonBestsLabel: meet.countsForSeasonBests === false ? 'Excluded' : 'Included',
  };
};

/**
 * @deprecated Use getMeetAgeGroupLabels() from utils/meet.js instead.
 * This function only supports legacy comma-separated integer format.
 *
 * Converts a comma-separated string of age group IDs into their corresponding labels.
 *
 * @param {string} ageGroups - A comma-separated string of age group IDs (e.g., "1,2,3").
 * @returns {Array<string>} - An array of age group labels corresponding to the provided IDs.
 */
export const getAgeGroupLabel = (ageGroups) => {
  const ageGroupLabels = [];

  ageGroups.split(',').forEach((ageGroup) => {
    const option = ageGroupOptions.find((option) => option.value === ageGroup);

    if (option) {
      ageGroupLabels.push(option.label);
    }
  });

  return ageGroupLabels;
};

/**
 * Converts event type IDs into their corresponding labels based on predefined options.
 *
 * This function takes a string of event type IDs and determines their labels using various predefined option arrays.
 * The labels are accumulated into the `eventTypeLabels` array and returned.
 *
 * @param {string} eventTypes - A comma-separated string of event type IDs (e.g., "101,202,303").
 * @returns {Array<string>} - An array of event type labels corresponding to the provided IDs.
 *
 * Example:
 * Input: "101,202"
 * Output: ["100m Sprint", "400m Hurdles"] (Assuming these IDs match the labels in the option arrays.)
 *
 * How It Works:
 * - Uses the helper function `pushEventTypeLabel` to match IDs from the `eventTypes` string against multiple
 *   predefined option arrays, including:
 *   - `multiLaneSprintEventOptions`
 *   - `hurdleEventOptions`
 *   - `relayEventOptions`
 *   - `groupDistanceEventOptions`
 *   - `walkEventOptions`
 *   - `sprintAgilityTrainingOptions`
 *   - `fieldEventOptions`
 *   - `statusOptions`
 * - Accumulates all matched labels into `eventTypeLabels` and returns it.
 *
 * Assumptions:
 * - Each option array is an array of objects where:
 *   - {string} value - The event type ID.
 *   - {string} label - The corresponding event type label.
 * - The `pushEventTypeLabel` function properly handles matching IDs and appending labels to the array.
 */

export const getEventTypeLabel = (
  eventTypes,
  isLabel2 = false,
  withValue = false
) => {
  const eventTypeLabels = [];

  pushEventTypeLabel(
    eventTypes,
    eventTypeLabels,
    multiLaneSprintEventOptions,
    isLabel2,
    withValue
  );
  pushEventTypeLabel(
    eventTypes,
    eventTypeLabels,
    hurdleEventOptions,
    isLabel2,
    withValue
  );
  pushEventTypeLabel(
    eventTypes,
    eventTypeLabels,
    relayEventOptions,
    isLabel2,
    withValue
  );
  pushEventTypeLabel(
    eventTypes,
    eventTypeLabels,
    groupDistanceEventOptions,
    isLabel2,
    withValue
  );
  pushEventTypeLabel(
    eventTypes,
    eventTypeLabels,
    walkEventOptions,
    isLabel2,
    withValue
  );
  pushEventTypeLabel(
    eventTypes,
    eventTypeLabels,
    sprintAgilityTrainingOptions,
    isLabel2,
    withValue
  );
  pushEventTypeLabel(
    eventTypes,
    eventTypeLabels,
    fieldEventOptions,
    isLabel2,
    withValue
  );
  pushEventTypeLabel(
    eventTypes,
    eventTypeLabels,
    statusOptions,
    isLabel2,
    withValue
  );

  return eventTypeLabels;
};

/**
 * Matches event type IDs with their corresponding labels and appends them to an array.
 *
 * This helper function takes a string of event type IDs, splits it into individual IDs,
 * and matches each ID against a provided set of event options. For each match, the corresponding
 * label is added to the `eventTypeLabels` array.
 *
 * @param {string} eventTypes - A comma-separated string of event type IDs (e.g., "101,202,303").
 * @param {Array<string>} eventTypeLabels - The array to which matched labels will be appended.
 * @param {Option[]} eventOptions - An array of objects representing event options,
 *   where each object contains:
 *   - {string} value: The event type ID.
 *   - {string} label: The corresponding event type label.
 *
 * Example:
 * Input:
 *   eventTypes: "101,202"
 *   eventTypeLabels: []
 *   eventOptions: [
 *     { value: "101", label: "100m Sprint" },
 *     { value: "202", label: "400m Hurdles" },
 *   ]
 * Output:
 *   eventTypeLabels: ["100m Sprint", "400m Hurdles"]
 *
 * How It Works:
 * - Splits the `eventTypes` string by commas to get individual event type IDs.
 * - Iterates over each ID and finds the corresponding object in `eventOptions` where the `value` matches the ID.
 * - If a match is found, its `label` is added to the `eventTypeLabels` array.
 *
 * Assumptions:
 * - `eventTypes` is a valid, comma-separated string of event type IDs.
 * - `eventOptions` contains unique mappings of `value` to `label`.
 */
export const pushEventTypeLabel = (
  eventTypes,
  eventTypeLabels,
  eventOptions,
  isLabel2 = false,
  withValue = false
) => {
  eventTypes.split(',').forEach((eventType) => {
    const option = eventOptions.find((option) => option.value === eventType);

    if (option) {
      const label = isLabel2 && option.label2 ? option.label2 : option.label;
      if (withValue) {
        eventTypeLabels.push({ value: eventType, label });
      } else {
        eventTypeLabels.push(label);
      }
    }
  });
};

/**
 * Transforms a result object into a formatted form data object.
 *
 * This function prepares the result data for use in a form by extracting and formatting
 * relevant fields from the provided `result` object. It handles splitting date, time,
 * and event types into structured properties suitable for form submission or UI rendering.
 *
 * @param {Meet} meet - The result object containing raw data, including the following fields:
 *   - {string} meetName: The name of the result.
 *   - {string} meetDate: The date and time of the result in ISO format (e.g., "2024-12-30T14:30:00").
 *   - {string} [timeZone]: The time zone of the result (e.g., "UTC").
 *   - {string} [description]: A description of the result.
 *   - {number} meetType: The type of the result (1 for Single Club Competition, 2 for Multi Club Competition, 3 for Training).
 *   - {string} location: The location of the result.
 *   - {string} [ageGroups]: Comma-separated age groups (e.g., "U10,U12").
 *   - {string} eventTypes: Comma-separated event type IDs (e.g., "20,23,41").
 *   - {string} meetStatus: The current status of the result (e.g., "Scheduled").
 *   - {boolean} countsForSeasonPoints: Whether this meet counts for season points.
 *   - {boolean} countsForSeasonBests: Whether this meet's results count for season bests (defaults to countsForSeasonPoints).
 *   - {Object} [defaultPointsRules]: The club's points rules (utils/pointsRules.js).
 *   - {Object|null} [customPointsRules]: The meet's own points rules, or null for the club's.
 *   - {string} [ageEventMapping]: The age event mapping configuration string.
 *
 * @returns {MeetFormData} A formatted form data object containing:
 *   - {string} meetName: The name of the result.
 *   - {string} date: The date of the result (e.g., "2024-12-30").
 *   - {string} time: The time of the result (e.g., "14:30").
 *   - {Object} timezone: An object with `id` and `displayName` for the time zone.
 *   - {string} description: The result description.
 *   - {string} meetType: The type of the result as a string (e.g., "1", "2", "3").
 *   - {string} location: The location of the result.
 *   - {Array<string>} ageGroups: An array of age group IDs.
 *   - {Array<string>} multiLaneEvents: Event type IDs for multi-lane events.
 *   - {Array<string>} hurdleEvents: Event type IDs for hurdle events.
 *   - {Array<string>} relayEvents: Event type IDs for relay events.
 *   - {Array<string>} groupDistanceEvents: Event type IDs for group distance events.
 *   - {Array<string>} walkEvents: Event type IDs for walk events.
 *   - {Array<string>} sprintAgilityTraining: Event type IDs for sprint/agility training.
 *   - {Array<string>} fieldEvents: Event type IDs for field events.
 *   - {string} meetStatus: The current status of the result.
 *   - {boolean} countsForSeasonPoints: Whether this meet counts for season points.
 *   - {boolean} countsForSeasonBests: Whether this meet's results count for season bests (defaults to countsForSeasonPoints).
 *   - {boolean} useCustomPoints: Whether the meet uses its own points (vs the club's).
 *   - {Object|null} defaultPointsRules: The club's points rules.
 *   - {Object|null} customPointsRules: The meet's own points rules, or null.
 *   - {string} ageEventMap: The age event mapping configuration string.
 *
 */
export const getMeetFormData = (meet) => {
  return {
    meetName: meet.meetName,
    date: meet.meetDate.split('T')[0],
    time: `${meet.meetDate.split('T')[1].split(':')[0]}:${meet.meetDate.split('T')[1].split(':')[1]}`,
    description: meet.description,
    meetType: meet.meetType.toString(),
    location: meet.location,
    meetStatus: meet.meetStatus,
    timezone: meet.timeZone || 'UTC', // read-only display; the meet inherits the club's timezone (§6.8 / D11)
    countsForSeasonPoints: meet.countsForSeasonPoints ?? true,
    countsForSeasonBests: meet.countsForSeasonBests ?? meet.countsForSeasonPoints ?? true,
    // The meet's points (docs/school-carnivals-points-slice-2.md in the portal repo): the club's
    // rules, and the meet's own, or null when it uses the club's.
    useCustomPoints: Boolean(meet.customPointsRules),
    defaultPointsRules: meet.defaultPointsRules || null,
    customPointsRules: meet.customPointsRules ? copyPointsRules(meet.customPointsRules) : null,
    highJumpSettings: meet.highJumpSettings ? {
      eliminationMode: meet.highJumpSettings.eliminationMode || 'consecutiveMisses',
      maxMisses: meet.highJumpSettings.maxMisses || 3,
      minimumStartHeights: convertHighJumpSettingsToUIFormat(meet.highJumpSettings.minimumStartHeights || []),
    } : {
      eliminationMode: 'consecutiveMisses',
      maxMisses: 3,
      minimumStartHeights: [],
    },
    // Meet signup config (§6.8). Window arrives as local wall-clock; split into date + time for the pickers.
    selfServiceRegistrationEnabled: meet.selfServiceRegistrationEnabled ?? false,
    publicSignupEnabled: meet.publicSignupEnabled ?? false,
    signupCode: meet.signupCode ?? null,
    signupOpenDate: meet.signupOpenAt ? meet.signupOpenAt.split('T')[0] : '',
    signupOpenTime: meet.signupOpenAt ? (meet.signupOpenAt.split('T')[1] || '').slice(0, 5) : '',
    signupCloseDate: meet.signupCloseAt ? meet.signupCloseAt.split('T')[0] : '',
    signupCloseTime: meet.signupCloseAt ? (meet.signupCloseAt.split('T')[1] || '').slice(0, 5) : '',
    registrationCap: meet.registrationCap ?? 0,
  };
};

/**
 * Converts high jump settings from API format (abbreviated: {age, f, m})
 * to internal format - API format is already correct, just pass through
 */
const convertHighJumpSettingsToUIFormat = (apiHeights) => {
  // API now returns the correct format, just return as-is
  return apiHeights;
};

/**
 * Transforms form data into a formatted result request object for submission.
 *
 * This function converts result form data into a structured object suitable
 * for backend API requests. It formats date and time, combines event types,
 * and prepares the necessary fields for the request payload.
 *
 * @param {MeetFormData} meetFormData - The form data object containing:
 *   - {string} meetName: The name of the result.
 *   - {'1' | '2' | '3'} meetType: The type of the result as a string (e.g., "1" for Single Club Competition, "3" for Training).
 *   - {string} date: The date of the result (e.g., "2024-12-30").
 *   - {string} time: The time of the result (e.g., "14:30").
 *   - {Object} timezone: An object with `id` and `displayName` for the time zone.
 *   - {string} location: The location of the result.
 *   - {string} description: A description of the result.
 *   - {string} meetStatus: The current status of the result (e.g., "Scheduled").
 *   - {Array<string>} ageGroups: An array of age group IDs.
 *   - {Array<string>} multiLaneEvents: Event type IDs for multi-lane events.
 *   - {Array<string>} hurdleEvents: Event type IDs for hurdle events.
 *   - {Array<string>} relayEvents: Event type IDs for relay events.
 *   - {Array<string>} groupDistanceEvents: Event type IDs for group distance events.
 *   - {Array<string>} walkEvents: Event type IDs for walk events.
 *   - {Array<string>} sprintAgilityTraining: Event type IDs for sprint/agility training.
 *   - {Array<string>} fieldEvents: Event type IDs for field events.
 *   - {boolean} countsForSeasonPoints: Whether this meet counts for season points.
 *   - {boolean} countsForSeasonBests: Whether this meet's results count for season bests (defaults to countsForSeasonPoints).
 *   - {boolean} useCustomPoints: Whether the meet uses its own points (vs the club's).
 *   - {Object|null} customPointsRules: The meet's own points rules.
 *   - {string} ageEventMap: The age event mapping configuration string.
 *
 * @returns {MeetRequest} A formatted result request object containing:
 *   - {string} meetName: The name of the result.
 *   - {'0' | '1'} meetType: The type of the result.
 *   - {string} meetDate: The combined date and time in ISO format (e.g., "2024-12-30T14:30").
 *   - {string} timeZone: The time zone ID.
 *   - {string} location: The location of the result.
 *   - {string} description: The description of the result.
 *   - {string} meetStatus: The current status of the result.
 *   - {string[]} ageGroups: Comma-separated age group IDs.
 *   - {string[]} eventTypes: Comma-separated event type IDs.
 *   - {boolean} countsForSeasonPoints: Whether this meet counts for season points.
 *   - {boolean} countsForSeasonBests: Whether this meet's results count for season bests (defaults to countsForSeasonPoints).
 *   - {Object|null} customPointsRules: The meet's own points rules, or null for the club's.
 *   - {string} ageEventMapping: The age event mapping configuration string.
 *
 * Example:
 * Input:
 *   meetFormData = {
 *     meetName: "Annual Sprint Meet",
 *     meetType: "1",
 *     date: "2024-12-30",
 *     time: "14:30",
 *     timezone: { id: "UTC", displayName: "UTC" },
 *     location: "Main Stadium",
 *     description: "End-of-year competition",
 *     meetStatus: "Scheduled",
 *     ageGroups: ["U10", "U12"],
 *     multiLaneEvents: ["20"],
 *     hurdleEvents: ["23"],
 *     relayEvents: [],
 *     groupDistanceEvents: [],
 *     walkEvents: ["41"],
 *     sprintAgilityTraining: [],
 *     fieldEvents: ["61"],
 *     countsForSeasonPoints: true,
 *     countsForSeasonBests: true,
 *     useCustomPoints: false,
 *     ageEventMap: "7:20,21|8:22,23",
 *   }
 * Output:
 *   {
 *     meetName: "Annual Sprint Meet",
 *     meetType: "1",
 *     meetDate: "2024-12-30T14:30",
 *     timeZone: "UTC",
 *     location: "Main Stadium",
 *     description: "End-of-year competition",
 *     meetStatus: "Scheduled",
 *     ageGroups: "U10,U12",
 *     eventTypes: "20,23,41,61",
 *     countsForSeasonPoints: true,
 *     countsForSeasonBests: true,
 *     customPointsRules: null,
 *     ageEventMapping: "7:20,21|8:22,23",
 *   }
 */
// The meet's own points as the API takes them, or null when it uses the club's. Also what the
// meet form compares to tell whether the points changed.
export const meetPointsRulesRequest = (meetFormData) =>
  meetFormData?.useCustomPoints && meetFormData.customPointsRules
    ? toPointsRulesRequest(meetFormData.customPointsRules)
    : null;

export const getMeetRequest = (meetFormData) => {
  // Build the base request
  const request = {
    meetName: (meetFormData.meetName || '').trim(),
    meetType: meetFormData.meetType,
    meetDate: `${meetFormData.date}T${meetFormData.time}`,
    timeZone: meetFormData.timezone || 'UTC', // per-meet, seeded from the club default (§6.8 / D11)
    location: meetFormData.location,
    description: meetFormData.description,
    meetStatus: meetFormData.meetStatus,
    countsForSeasonPoints: meetFormData.countsForSeasonPoints ?? true,
    countsForSeasonBests: meetFormData.countsForSeasonBests ?? meetFormData.countsForSeasonPoints ?? true,
  };

  // The meet's own points, or null for the club's (independent of countsForSeasonPoints).
  request.customPointsRules = meetPointsRulesRequest(meetFormData);

  // Add high jump settings if provided
  if (meetFormData.highJumpSettings) {
    request.highJumpSettings = {
      eliminationMode: meetFormData.highJumpSettings.eliminationMode || 'consecutiveMisses',
      maxMisses: Number(meetFormData.highJumpSettings.maxMisses) || 3,
      minimumStartHeights: meetFormData.highJumpSettings.minimumStartHeights || [],
    };
  }

  // Meet signup config (§6.8). Window combined into local wall-clock (date + time), same convention as meetDate;
  // null when no date is set. SignupCode is server-minted and never sent. Server ignores all of this unless multi-club.
  const combineDateTime = (date, time, fallbackTime) =>
    date ? `${date}T${time || fallbackTime}` : null;

  request.selfServiceRegistrationEnabled = !!meetFormData.selfServiceRegistrationEnabled;
  request.publicSignupEnabled = !!meetFormData.publicSignupEnabled;
  request.registrationCap = Number(meetFormData.registrationCap) || 0;
  request.signupOpenAt = combineDateTime(meetFormData.signupOpenDate, meetFormData.signupOpenTime, '00:00');
  request.signupCloseAt = combineDateTime(meetFormData.signupCloseDate, meetFormData.signupCloseTime, '23:59');

  return request;
};

/**
 * Extracts unique age groups and event types from an ageEventMap string.
 *
 * @param {string} ageEventMap - The age event mapping string (e.g., "7:20,21|8:22,23")
 * @returns {Object} An object containing arrays of unique age groups and event types
 *   - {Array<string>} ageGroups: Array of unique age group IDs
 *   - {Array<string>} eventTypes: Array of unique event type IDs
 */
export const extractAgeGroupsAndEventsFromMap = (ageEventMap) => {
  const ageGroups = new Set();
  const eventTypes = new Set();

  if (!ageEventMap || ageEventMap.trim() === '') {
    return { ageGroups: [], eventTypes: [] };
  }

  try {
    // Split by pipe to get age group pairs
    const ageGroupPairs = ageEventMap.split('|');

    ageGroupPairs.forEach((pair) => {
      const [ageGroup, events] = pair.split(':');

      // Add age group if it exists and has events
      if (ageGroup && events && events.trim() !== '') {
        ageGroups.add(ageGroup.trim());

        // Add each event type
        events.split(',').forEach((event) => {
          const trimmedEvent = event.trim();
          if (trimmedEvent) {
            eventTypes.add(trimmedEvent);
          }
        });
      }
    });
  } catch (err) {
    console.error('Error parsing ageEventMap:', err);
  }

  return {
    ageGroups: Array.from(ageGroups),
    eventTypes: Array.from(eventTypes),
  };
};

/**
 * Transforms meet registration data into a format suitable for AdminDataTable display.
 *
 * This function takes an array of meet registration objects and transforms them into a structured
 * format that can be used with the AdminDataTable component. It extracts athlete information,
 * creates event checkboxes based on registered events, and groups data by age group and gender.
 *
 * @param {Array<Object>} registrations - Array of registration objects containing:
 *   - {string} registrationID - Unique registration identifier
 *   - {string} meetID - Meet identifier
 *   - {string} athleteID - Athlete identifier
 *   - {string} memberType - Type of member (Full, Guest, etc.)
 *   - {boolean} isIndividualGuest - Whether this is an individual guest registration
 *   - {string} clubName - Name of the athlete's club
 *   - {string} firstName - Athlete's first name
 *   - {string} lastName - Athlete's last name
 *   - {number} ageGroup - Age group number
 *   - {string} gender - Gender (M/F)
 *   - {number} clubAthleteNumber - Club-assigned athlete number
 *   - {string} registrationStatus - Current registration status
 *   - {Array<Object>} eventRegistrations - Array of event registration objects
 * @param {number} groupingBasis - How the meet's club groups its athletes (GROUPING_BASIS in
 *   utils/groupLabel.js). It decides how a group's label reads: "U11 M" for an Under club, "11 M" for an
 *   Age club. A caller that passes nothing gets the Under labels.
 *
 * @returns {Object} An object containing:
 *   - {Array<Object>} athletes - Transformed athlete data for table display
 *   - {Array<Object>} ageGroupTabs - Age group tabs configuration
 *   - {Object} clubSummary - Club participation summary
 */
export const transformMeetRegistrationData = (
  registrations = [],
  groupingBasis = GROUPING_BASIS.UNDER
) => {
  const eventCodeMap = {};
  const eventDetails = {};

  registrations.forEach((registration) => {
    registration.eventRegistrations.forEach((eventReg) => {
      if (!eventCodeMap[eventReg.eventCode]) {
        // Create a property name from event name (e.g., "100m" -> "event100M")
        const propertyName = `event${eventReg.eventName.replace(/\s+/g, '').toUpperCase()}`;
        eventCodeMap[eventReg.eventCode] = propertyName;
        eventDetails[eventReg.eventCode] = {
          name: eventReg.eventName.toUpperCase(),
          propertyName: propertyName,
          rotationOrder: eventReg.rotationOrder,
        };
      }
    });
  });

  const initializeEvents = () => {
    return Object.values(eventCodeMap).reduce((acc, eventKey) => {
      acc[eventKey] = false;
      return acc;
    }, {});
  };

  const athletes = registrations.map((registration) => {
    const events = initializeEvents();

    registration.eventRegistrations.forEach((eventReg) => {
      const eventKey = eventCodeMap[eventReg.eventCode];
      if (eventKey) {
        events[eventKey] = true;
      }
    });

    return {
      id: registration.registrationID,
      athleteId: registration.athleteID,
      name: `${registration.firstName} ${registration.lastName}`,
      firstName: registration.firstName,
      lastName: registration.lastName,
      club: registration.clubName,
      number: registration.clubAthleteNumber?.toString() || '---',
      ageGroup: registration.ageGroup,
      gender: registration.gender,
      memberType: registration.memberType,
      isIndividualGuest: registration.isIndividualGuest,
      registrationStatus: registration.registrationStatus,
      ...events,
    };
  });

  // Group athletes by age group and gender. The key ("u11m") is an id that
  // other code parses, so it keeps its "u" for every club; only the label
  // below is read by a user.
  const groupedAthletes = {};
  athletes.forEach((athlete) => {
    const ageGroupKey = `u${athlete.ageGroup}${athlete.gender.toLowerCase()}`;
    if (!groupedAthletes[ageGroupKey]) {
      groupedAthletes[ageGroupKey] = [];
    }
    groupedAthletes[ageGroupKey].push(athlete);
  });

  // Create age group tabs
  const ageGroupTabs = Object.keys(groupedAthletes)
    .sort()
    .map((key) => {
      const athletes = groupedAthletes[key];
      const ageGroup = athletes[0].ageGroup;
      const gender = athletes[0].gender;
      const genderLabel = gender === 'M' ? 'M' : 'F';

      return {
        id: key,
        label: `${formatGroupHeading(groupingBasis, ageGroup)} ${genderLabel}`,
        count: athletes.length,
      };
    });

  return {
    athletes: groupedAthletes,
    ageGroupTabs,
    eventDetails: eventDetails,
    eventCodeMap: eventCodeMap,
  };
};

/**
 * Transforms meet registration data into AdminDataTable format following the AthletePastMeet pattern.
 *
 * This function takes an array of athlete registration objects and transforms them into a format
 * suitable for AdminDataTable display, including custom Cell components for event checkboxes
 * and action buttons.
 *
 * @param {Array<Object>} athletes - Array of athlete objects from transformMeetRegistrationData
 * @param {Object} eventDetails - Event details object with event codes and names
 * @param {Function} onCheckboxChange - Callback function to handle checkbox changes
 * @returns {Array<Object>} Transformed data array for AdminDataTable
 */
export const meetRegistrationTableData = (
  athletes = [],
  eventDetails = {},
  onCheckboxChange = null,
  editable = false
) => {
  return athletes.map((athlete) => {
    const baseData = {
      ...athlete,
      athlete: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div>
            <div style={{ fontWeight: '500', marginBottom: '2px' }}>
              {athlete.name}
            </div>
          </div>
        </div>
      ),
      club: athlete.club,
      number: athlete.number,
    };

    // Dynamically generate event checkboxes using custom Cell renderers
    const eventCheckboxes = {};
    Object.values(eventDetails).forEach((event) => {
      eventCheckboxes[event.propertyName] = (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {editable ? (
            <input
              type="checkbox"
              checked={!!athlete?.[event.propertyName]}
              onChange={(e) => {
                if (onCheckboxChange) {
                  onCheckboxChange({
                    athleteId: athlete.athleteId,
                    registrationId: athlete.id,
                    eventCode: event.propertyName,
                    checked: e.target.checked,
                  });
                } else {
                  console.log('Checkbox changed:', {
                    athleteId: athlete.athleteId,
                    eventCode: event.propertyName,
                    checked: e.target.checked,
                  });
                }
              }}
              style={{
                width: '16px',
                height: '16px',
                cursor: 'pointer',
              }}
            />
          ) : !!athlete?.[event.propertyName] ? (
            <span
              style={{
                display: 'inline-block',
                width: '8px',
                height: '8px',
                background: '#10b981',
                borderRadius: '50%',
              }}
            />
          ) : null}
        </div>
      );
    });

    // Add actions
    const actions = (
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
        <button
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
            padding: '4px',
            transition: 'transform 0.2s ease',
            color: '#3b82f6',
          }}
          title="Edit"
          onMouseEnter={(e) => (e.target.style.transform = 'scale(1.2)')}
          onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
        >
          ✏️
        </button>
        <button
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
            padding: '4px',
            transition: 'transform 0.2s ease',
            color: '#ef4444',
          }}
          title="Remove"
          onMouseEnter={(e) => (e.target.style.transform = 'scale(1.2)')}
          onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
        >
          ❌
        </button>
      </div>
    );

    return {
      ...baseData,
      ...eventCheckboxes,
      actions,
    };
  });
};

/**
 * Generates column configuration for AdminDataTable based on event details.
 *
 * @param {Object} eventDetails - Event details object with event codes and names
 * @returns {Array<Object>} Column configuration array for AdminDataTable
 */
export const generateMeetRegistrationColumns = (
  eventDetails = {},
  hasAction = false
) => {
  const { tableHeaderStyle, tableBodyStyle } = require('config/global');

  const baseColumns = [
    {
      accessor: 'athlete',
      label: 'ATHLETE',
      filterAccessor: 'name',
      headerStyle: { ...tableHeaderStyle, minWidth: '154px' },
      style: { ...tableBodyStyle, width: '1.5fr', minWidth: '154px' },
    },
    {
      accessor: 'club',
      label: 'CLUB',
      filterAccessor: 'club',
      headerStyle: { ...tableHeaderStyle, minWidth: '154px' },
      style: { ...tableBodyStyle, width: '1.3fr', minWidth: '154px' },
    },
    {
      accessor: 'number',
      label: 'NUMBER',
      filterAccessor: 'number',
      headerStyle: { ...tableHeaderStyle, minWidth: '58px' },
      style: { ...tableBodyStyle, width: '1fr', minWidth: '58px' },
    },
  ];

  const eventColumns = Object.values(eventDetails)
    .sort((a, b) => a.rotationOrder - b.rotationOrder) // Sort by rotation order
    .map((event) => ({
      accessor: event.propertyName,
      label: event.name,
      headerStyle: {
        textAlign: 'left',
        border: 'none',
        padding: '0',
        minWidth: '58px',
      },
      style: {
        width: '1fr',
        textAlign: 'left',
        border: 'none',
        padding: '0',
        minWidth: '58px',
      },
    }));

  if (hasAction) {
    // Add actions column
    const actionsColumn = {
      accessor: 'actions',
      label: 'ACTION',
      headerStyle: {
        ...tableHeaderStyle,
        textAlign: 'center',
        minWidth: '90px',
      },
      style: {
        ...tableBodyStyle,
        width: '1fr',
        textAlign: 'center',
        minWidth: '90px',
      },
    };

    return [...baseColumns, ...eventColumns, actionsColumn];
  }
  return [...baseColumns, ...eventColumns];
};
